// @core/stores/affiliate/AffiliateOfferStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { affiliateApi } from '@core/api';
import type {
  ActivateOfferRequest,
  CreateOfferRequest,
  GetOfferStatsResponse,
  Offer,
  OfferResponse,
  PauseOfferRequest,
  UpdateOfferRequest,
} from '@core/types/affiliate';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class AffiliateOfferStore {
  // List state
  items: Offer[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Offer | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Offer>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Offer | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Offer | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Activate offer
   */
  async ActivateOffer(offer_id: string, data: ActivateOfferRequest): Promise<OfferResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await affiliateApi.ActivateOffer(offer_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create offer
   */
  async CreateOffer(data: CreateOfferRequest): Promise<Offer> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await affiliateApi.CreateOffer(data);
      const item = response.offer;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Get offer by ID
   */
  async GetOffer(offer_id: string): Promise<Offer> {
    // Check cache first
    const cached = this.cache.get(offer_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await affiliateApi.GetOffer(offer_id);
      const item = response.offer;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get offer statistics
   */
  async GetOfferStats(offer_id: string, params?: { period_end?: string | number | boolean; period_start?: string | number | boolean; }): Promise<GetOfferStatsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await affiliateApi.GetOfferStats(offer_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List offers
   */
  async ListOffers(params?: PaginatedRequest & { payout_type?: string | number | boolean; q?: string | number | boolean; status?: string | number | boolean; }): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await affiliateApi.ListOffers(params);

      runInAction(() => {
        this.items = response.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Pause offer
   */
  async PauseOffer(offer_id: string, data: PauseOfferRequest): Promise<OfferResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await affiliateApi.PauseOffer(offer_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update offer
   */
  async UpdateOffer(offer_id: string, data: UpdateOfferRequest): Promise<Offer> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await affiliateApi.UpdateOffer(offer_id, data);
      const item = response.offer;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const affiliateOfferStore = new AffiliateOfferStore();
